import { Body2 } from '@components/typography/Body2.tsx';
import {
  type ComponentType,
  type PropsWithChildren,
  useCallback,
  useState,
} from 'react';
import { InlineEdit, type InlineEditProps } from './base/InlineEdit';
import {
  TextInput,
  type TextInputProps,
} from '@components/input/TextInput.tsx';
import { classNames } from '@utils/classNames.ts';
import { useTranslation } from 'react-i18next';

export type InlineTextEditProps = {
  value: string;
  onSubmit: (value: string) => Promise<unknown>;
  placeholder?: string;
  className?: string;
  classNameEditingContainer?: string;
  allowEmptyValue?: boolean;
  ValueComponent?: ComponentType<PropsWithChildren & { className?: string }>;
  testId?: string;
  emptyDisplayValue?: string;
  showButtons?: boolean;
  classNameInput?: string;
  saveOnBlur?: boolean;
  size?: 'small' | 'medium';
  disabled?: boolean;
  passthrough?: {
    input?: TextInputProps<string>['passthrough'];
    inlineEdit?: InlineEditProps['passthrough'];
  };
} & Pick<TextInputProps<string>, 'validation' | 'showError' | 'onChange'> &
  Partial<Pick<InlineEditProps, 'size'>>;

export function InlineTextEdit({
  value,
  onSubmit,
  placeholder,
  className,
  classNameEditingContainer,
  allowEmptyValue = true,
  ValueComponent = Body2,
  testId,
  emptyDisplayValue,
  showButtons = true,
  classNameInput,
  saveOnBlur = false,
  showError = false,
  onChange,
  children,
  size,
  passthrough,
  disabled,
  validation,
  ...rest
}: PropsWithChildren<InlineTextEditProps>) {
  const [editedValue, setEditedValue] = useState(value);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation('common');

  const cancelEditing = useCallback(() => {
    setEditedValue(value);
    setIsEditing(false);
  }, [value]);

  const submitEditedValue = useCallback(async () => {
    if (validation && !validation?.isValid) {
      return;
    }
    setError(null);
    if (editedValue === value) {
      setIsEditing(false);
      return;
    }

    if (!allowEmptyValue && editedValue.trim() === '') {
      cancelEditing();
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(editedValue);
      setIsEditing(false);
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : t('errors.unknown');
      setError(t('errors.errorWhileSaving', { errorMessage }));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    allowEmptyValue,
    cancelEditing,
    editedValue,
    onSubmit,
    t,
    validation,
    value,
  ]);

  return (
    <InlineEdit
      onSubmit={submitEditedValue}
      disabled={disabled}
      onCancel={cancelEditing}
      className={className}
      classNameEditingContainer={classNameEditingContainer}
      showButtons={showButtons}
      isEditing={isEditing}
      setIsEditing={setIsEditing}
      size={size}
      testId={testId}
      passthrough={passthrough?.inlineEdit}
      ValueComponent={
        children ?? (
          <ValueComponent className="break-words line-clamp-2">
            {value || emptyDisplayValue}
          </ValueComponent>
        )
      }
      EditComponent={
        <TextInput
          value={editedValue}
          onChange={(newValue: string) => {
            setEditedValue(newValue);
            onChange?.(newValue);
          }}
          onBlur={saveOnBlur ? submitEditedValue : cancelEditing}
          placeholder={placeholder}
          autoFocus
          classNameContainer={classNames(
            className,
            'flex-1',
            showButtons && 'w-full',
          )}
          classNameInput={classNames('!p-1 !rounded bg-white', classNameInput)}
          disabled={isSubmitting}
          onEnter={submitEditedValue}
          onEsc={cancelEditing}
          showError={showError || !!error}
          additionalErrorMessage={error}
          inputTestId={`${testId}-input`}
          passthrough={passthrough?.input}
          validation={validation}
          {...rest}
        />
      }
    />
  );
}
